// Code generated by @sumup/sumup-ts-codegen. DO NOT EDIT.

import * as Core from "./core";
import { buildRuntimeHeaders } from "./runtime";
import { VERSION } from "./version";

export type APIConfig = {
  apiKey?: string;
  host?: string;
  baseParams?: Core.RequestOptions;
  maxRetries?: number;
  timeout?: number;
};

export class HTTPClient {
  host: string;
  apiKey?: string;
  baseParams: Core.RequestOptions;

  constructor({
    apiKey,
    host = "https://api.sumup.com",
    baseParams = {},
    maxRetries = 0,
    timeout,
  }: APIConfig = {}) {
    this.host = host;
    this.apiKey = apiKey;

    const headers = new Headers({
      "Accept": "application/problem+json, application/json",
      "Content-Type": "application/json",
      "User-Agent": `sumup-ts/v${VERSION}`,
      ...buildRuntimeHeaders(),
    });
    if (apiKey) {
      headers.append("Authorization", `Bearer ${apiKey}`);
    }
    this.baseParams = mergeParams({ headers, maxRetries, timeout }, baseParams);
  }

  public get<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<R> {
    return this.request<R>({
      method: "GET",
      ...params,
    });
  }

  public getWithResponse<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<Core.WithResponse<R>> {
    return this.requestWithResponse<R>({
      method: "GET",
      ...params,
    });
  }

  public post<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<R> {
    return this.request<R>({
      method: "POST",
      ...params,
    });
  }

  public postWithResponse<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<Core.WithResponse<R>> {
    return this.requestWithResponse<R>({
      method: "POST",
      ...params,
    });
  }

  public put<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<R> {
    return this.request<R>({
      method: "PUT",
      ...params,
    });
  }

  public putWithResponse<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<Core.WithResponse<R>> {
    return this.requestWithResponse<R>({
      method: "PUT",
      ...params,
    });
  }

  public patch<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<R> {
    return this.request<R>({
      method: "PATCH",
      ...params,
    });
  }

  public patchWithResponse<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<Core.WithResponse<R>> {
    return this.requestWithResponse<R>({
      method: "PATCH",
      ...params,
    });
  }

  public delete<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<R> {
    return this.request<R>({
      method: "DELETE",
      ...params,
    });
  }

  public deleteWithResponse<R>({
    ...params
  }: Omit<Core.FullRequestOptions, "method">): Promise<Core.WithResponse<R>> {
    return this.requestWithResponse<R>({
      method: "DELETE",
      ...params,
    });
  }

  public request<T>({ ...params }: Core.FullRequestOptions): Promise<T> {
    return this.requestWithResponse<T>(params).then(({ data }) => data);
  }

  public async requestWithResponse<T>({
    body,
    path,
    query,
    host: hostOverride,
    ...requestOptions
  }: Core.FullRequestOptions): Promise<Core.WithResponse<T>> {
    const host = hostOverride || this.host;
    const url = new URL(
      host +
        (host.endsWith("/") && path.startsWith("/") ? path.slice(1) : path),
    );
    if (typeof query === "object" && query && !Array.isArray(query)) {
      url.search = this.stringifyQuery(query as Record<string, unknown>);
    }
    const mergedOptions = mergeParams(this.baseParams, requestOptions);
    const { maxRetries, timeout, ...fetchParams } = mergedOptions;
    const init = {
      ...fetchParams,
      body: JSON.stringify(body),
    };
    const response = await this.do(url, init, {
      maxRetries,
      signal: fetchParams.signal,
      timeout,
    });
    const data = await Core.parseResponse<T>(response.clone());

    return { data, response };
  }

  protected async do(
    input: URL,
    init: RequestInit,
    options: {
      maxRetries?: number;
      signal?: AbortSignal | null;
      timeout?: number;
    },
  ): Promise<Response> {
    const maxRetries = options.maxRetries ?? 0;

    for (let attempt = 0; ; attempt++) {
      const { cleanup, didTimeout, signal } = withTimeoutSignal(
        options.signal,
        options.timeout,
      );

      try {
        const res = await fetch(input, { ...init, signal });

        if (!res.ok) {
          if (attempt < maxRetries && isRetryableStatus(res.status)) {
            continue;
          }

          const contentType = res.headers.get("content-type");
          const isJSON = contentType?.includes("json");
          throw new Core.APIError(
            res.status,
            isJSON ? await res.json() : await res.text(),
            res,
          );
        }

        return res;
      } catch (error) {
        if (attempt < maxRetries && isRetryableError(error, options.signal)) {
          continue;
        }
        if (didTimeout()) {
          throw new Core.SumUpError(
            `Request timed out after ${options.timeout}ms.`,
          );
        }
        throw error;
      } finally {
        cleanup();
      }
    }
  }

  protected stringifyQuery(query: Record<string, unknown>): string {
    return Object.entries(query)
      .filter(([_, value]) => typeof value !== "undefined")
      .map(([key, value]) => {
        if (
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean"
        ) {
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
        if (value === null) {
          return `${encodeURIComponent(key)}=`;
        }
        if (Array.isArray(value)) {
          return value
            .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
            .join("&");
        }
        throw new Error(
          `Cannot stringify type ${typeof value}; Expected string, number, boolean, or null.`,
        );
      })
      .join("&");
  }
}

export function mergeParams(
  a: Core.RequestOptions,
  b: Core.RequestOptions,
): Core.RequestOptions {
  const {
    authorization: defaultAuthorization,
    headers: defaultHeaders,
    ...defaultParams
  } = a;
  const {
    authorization: overrideAuthorization,
    headers: overrideHeaders,
    ...overrideParams
  } = b;
  // calling `new Headers()` normalizes `HeadersInit`, which could be a Headers
  // object, a plain object, or an array of tuples
  const headers = new Headers(defaultHeaders);
  for (const [key, value] of new Headers(overrideHeaders).entries()) {
    headers.set(key, value);
  }
  const authorization = overrideAuthorization ?? defaultAuthorization;
  if (authorization) {
    headers.set("Authorization", authorization);
  }

  return { ...defaultParams, ...overrideParams, headers };
}

function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 409 || status === 429 || status >= 500;
}

function isRetryableError(
  error: unknown,
  signal?: AbortSignal | null,
): boolean {
  if (signal?.aborted) {
    return false;
  }
  return error instanceof TypeError || isAbortError(error);
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

function withTimeoutSignal(
  signal?: AbortSignal | null,
  timeout?: number,
): {
  cleanup: () => void;
  didTimeout: () => boolean;
  signal: AbortSignal | undefined;
} {
  if (!signal && typeof timeout === "undefined") {
    return {
      cleanup: () => {},
      didTimeout: () => false,
      signal: undefined,
    };
  }

  const controller = new AbortController();
  let timedOut = false;

  const onAbort = () => {
    controller.abort(signal?.reason);
  };

  if (signal) {
    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener("abort", onAbort, { once: true });
    }
  }

  const timeoutId =
    typeof timeout === "number"
      ? setTimeout(() => {
          timedOut = true;
          controller.abort(
            new Core.SumUpError(`Request timed out after ${timeout}ms.`),
          );
        }, timeout)
      : undefined;

  return {
    cleanup: () => {
      if (typeof timeoutId !== "undefined") {
        clearTimeout(timeoutId);
      }
      signal?.removeEventListener("abort", onAbort);
    },
    didTimeout: () => timedOut,
    signal: controller.signal,
  };
}
